export interface ChartBuffers {
  topBuffer: number;
  bottomBuffer: number;
}

export interface ChartPoint {
  x: Date;
  y: number;
}

export interface ChartData {
  // aligned, valid timestamps (ms) — same length/order as `equities`
  dates: number[];
  // aligned, 2-decimal equity values — same length/order as `dates`
  equities: number[];
  // normalized {x, y} points built from the same aligned source
  points: ChartPoint[];
  maxY: number;
  minY: number;
}

// Map a raw equity value into the buffered 0-100 range used by the y-axis.
export const normalizeEquityValue = (
  value: number,
  minY: number,
  maxY: number,
  { topBuffer, bottomBuffer }: ChartBuffers,
): number => {
  const range = maxY - minY;
  // Flat data (all values equal): pin the line to the middle of the range.
  if (range === 0) {
    return 50;
  }
  const percent = (value - minY) / range;
  return bottomBuffer + percent * (100 - topBuffer - bottomBuffer);
};

// Inverse of `normalizeEquityValue`: map a buffered 0-100 value back to dollars.
export const denormalizeEquityValue = (
  value: number,
  minY: number,
  maxY: number,
  { topBuffer, bottomBuffer }: ChartBuffers,
): number => {
  const range = maxY - minY;
  if (range === 0) {
    return maxY;
  }
  const percent = (value - bottomBuffer) / (100 - topBuffer - bottomBuffer);
  return minY + percent * range;
};

// Build the chart's dates/equities/points from a SINGLE aligned source.
//
// The portfolio-history endpoint returns two parallel arrays (`timestamp` and
// `equity`). Filtering them independently lets them drift out of alignment, and
// building points by index then dereferences `dates[index]` for indices that
// don't exist (or an `Invalid Date`), which surfaces as Chart.js "reading 'x'"
// crashes during decimation/scale computation. Zipping first and keeping only
// pairs where BOTH values are finite (and the timestamp is a valid date)
// guarantees every emitted point has a real `x` and `y`.
export const buildChartData = (
  timestamps: number[],
  equitiesInput: number[],
  buffers: ChartBuffers,
): ChartData => {
  const length = Math.min(timestamps.length, equitiesInput.length);

  const aligned: { ts: number; equity: number; date: Date }[] = [];
  for (let i = 0; i < length; i++) {
    const rawTs = timestamps[i];
    const rawEquity = equitiesInput[i];

    // Drop pairs where either side is missing. `Number(null)` is `0` (finite),
    // so a null must be rejected before coercion or it becomes a 1970 point.
    if (rawTs == null || rawEquity == null) {
      continue;
    }

    const ts = Number(rawTs);
    const equityValue = Number(rawEquity);

    // Drop pairs where either side is NaN/±Infinity.
    if (!Number.isFinite(ts) || !Number.isFinite(equityValue)) {
      continue;
    }

    // Drop pairs whose timestamp can't form a valid Date.
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) {
      continue;
    }

    aligned.push({
      ts,
      equity: Number.parseFloat(equityValue.toFixed(2)),
      date,
    });
  }

  const dates = aligned.map((p) => p.ts);
  const equities = aligned.map((p) => p.equity);

  // Compute min/max in a single pass instead of spreading into Math.min/max,
  // which can throw a RangeError (max argument count) on very large arrays.
  let maxY = 0;
  let minY = 0;
  if (equities.length > 0) {
    maxY = equities[0];
    minY = equities[0];
    for (const value of equities) {
      if (value > maxY) {
        maxY = value;
      }
      if (value < minY) {
        minY = value;
      }
    }
  }

  const points = aligned.map((p) => ({
    x: p.date,
    y: normalizeEquityValue(p.equity, minY, maxY, buffers),
  }));

  return { dates, equities, points, maxY, minY };
};
